<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit User' : 'Add User'" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Full Name</label>
              <input type="text" class="form-control" v-model="form.name" placeholder="Enter full name" required />
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Email Address</label>
              <input type="email" class="form-control" v-model="form.email" placeholder="Enter email address" required />
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Role</label>
              <select class="form-select" v-model="form.role" required>
                <option value="" disabled>Select Role</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Password</label>
              <input type="password" class="form-control" v-model="form.password" :placeholder="isEditing ? 'Leave blank to keep current' : 'Enter password'" :required="!isEditing" />
              <small v-if="isEditing" class="text-secondary">Leave blank if you don't want to change the password.</small>
            </div>
          </div>
          
          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/users" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update User' : 'Create User' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import userService from '@/services/userService';

export default defineComponent({
  name: 'UserFormPage',
  components: {
    PageTitle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    const isEditing = ref(false);
    const userId = ref<number | null>(null);
    const loading = ref(false);
    
    const form = ref({
      name: '',
      email: '',
      role: '',
      password: ''
    });

    onMounted(async () => {
      if (route.params.id) {
        isEditing.value = true;
        userId.value = Number(route.params.id);
        await fetchUser(userId.value);
      }
    });

    const fetchUser = async (id: number) => {
      try {
        const response = await userService.getById(id);
        const data = response.data;
        form.value = {
          name: data.name,
          email: data.email,
          role: data.role,
          password: ''
        };
      } catch (error) {
        console.error('Error fetching user:', error);
        router.push('/users');
      }
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        const payload: any = {
          name: form.value.name,
          email: form.value.email,
          role: form.value.role,
        };
        if (form.value.password) {
          payload.password = form.value.password;
        }

        if (isEditing.value && userId.value) {
          await userService.update(userId.value, payload);
          
          // If editing self, update the local storage user object
          const currentUserStr = localStorage.getItem('user');
          if (currentUserStr) {
            try {
              const currentUser = JSON.parse(currentUserStr);
              if (currentUser.id === userId.value) {
                currentUser.name = payload.name;
                currentUser.email = payload.email;
                currentUser.role = payload.role;
                localStorage.setItem('user', JSON.stringify(currentUser));
              }
            } catch (e) {
              console.error(e);
            }
          }
        } else {
          await userService.create(payload);
        }
        router.push('/users');
      } catch (error: any) {
        console.error('Error saving user:', error);
        if (error.response?.data?.errors) {
          const firstError = Object.values(error.response.data.errors)[0] as string[];
          alert(firstError[0]);
        } else if (error.response?.data?.message) {
          alert(error.response.data.message);
        } else {
          alert('Failed to save user.');
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      isEditing,
      form,
      loading,
      submitForm
    };
  }
});
</script>
